/**
 * Akte auf dem Gerät (27.10.2026).
 *
 * Gewöhnliche Sync-Zeilen (`record_entries`): lokal zuerst, dann die Outbox —
 * dort Klartext, hinaus nur verschlüsselt (`ALWAYS_SEALED_TABLES`; ohne
 * Schlüssel wartet die Outbox). Wer was sieht, entscheidet der Server beim
 * Holen: Auf dem Gerät liegt nur, was es sehen darf.
 *
 * - Eigene Akte: ohne Haushalt, `visibility = 'me'`.
 * - Akte eines Kindes: im Haushalt, `adults` oder `child`. Die Sichtbarkeit
 *   ändern heißt neu anlegen und den alten Eintrag löschen — der Server lässt
 *   einen Eintrag nicht umziehen, sonst erführe das Kind nie, dass es ihn
 *   nicht mehr sehen darf.
 * - Fristen landen über die vorhandenen Schreibfunktionen in der Planung
 *   (Aufgabe oder Termin, in **einem** `writeRows` mit dem Verweis am Eintrag).
 */

import { cleanRecordFields, newId, recordDue } from '@kraftwerk/shared';
import type { RecordKind, RecordPlan, RecordVisibility } from '@kraftwerk/shared';
import { KEY_META } from '../crypto/e2e.js';
import { db } from './local.js';
import type {
  KraftwerkDatabase,
  LocalEvent,
  LocalHouseholdEvent,
  LocalRecordEntry,
  LocalTask,
  LocalTaskList,
} from './local.js';
import { stamp, writeRow, writeRows } from './write.js';
import type { RowChange } from './write.js';

/** Einträge über eine Person — was hier liegt, durfte das Gerät holen. */
export async function recordsOf(
  subjectId: string,
  /** `null`: nur die eigene (privat), fehlt: jede, die auf dem Gerät liegt. */
  householdId?: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalRecordEntry[]> {
  return (await database.record_entries.where('subjectId').equals(subjectId).toArray()).filter(
    (entry) =>
      !entry.deletedAt &&
      (householdId === undefined || (entry.householdId ?? null) === householdId),
  );
}

export interface RecordDraft {
  householdId: string | null;
  subjectId: string;
  visibility: RecordVisibility;
  kind: RecordKind;
  fields: Record<string, string>;
}

/** Ungültige Felder (Name fehlt, Datum kaputt, zu lang) — nicht gespeichert. */
export class InvalidRecord extends Error {}

/**
 * Anlegen oder ändern. Geänderte Sichtbarkeit: neuer Eintrag (mit dem
 * Verweis in die Planung), der alte weich gelöscht — in einem Schritt.
 */
export async function saveRecordEntry(
  draft: RecordDraft,
  existing: LocalRecordEntry | null = null,
  database: KraftwerkDatabase = db,
): Promise<LocalRecordEntry> {
  const fields = cleanRecordFields(draft.kind, draft.fields);
  if (!fields) throw new InvalidRecord();
  const visibility: RecordVisibility = draft.householdId
    ? draft.visibility === 'me'
      ? 'adults'
      : draft.visibility
    : 'me';
  const now = stamp();
  const keepId = existing && existing.visibility === visibility;
  const row: LocalRecordEntry = {
    ...(keepId ? existing : {}),
    id: keepId ? existing.id : newId(),
    householdId: draft.householdId,
    subjectId: draft.subjectId,
    visibility,
    kind: draft.kind,
    fields,
    plan: existing?.plan ?? null,
    updatedAt: now,
    deletedAt: null,
  };
  const changes: RowChange[] = [{ table: 'record_entries', row }];
  if (existing && !keepId) {
    changes.unshift({
      table: 'record_entries',
      row: { ...existing, updatedAt: now, deletedAt: now },
      kind: 'delete',
    });
  }
  await writeRows(changes, database);
  return row;
}

export async function deleteRecordEntry(
  entry: LocalRecordEntry,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const now = stamp();
  await writeRow(
    'record_entries',
    { ...entry, updatedAt: now, deletedAt: now },
    'delete',
    database,
  );
}

export interface PlanDraft {
  as: 'task' | 'event';
  /** Der Titel in der Planung — der Server liest ihn für die Mitteilung. */
  title: string;
  /** An welchem Tag (Vorgabe: die Frist). */
  date: string;
  /** Uhrzeit der Aufgabe — erst damit kommt eine Mitteilung. */
  time: string | null;
  /** Name einer neuen Liste, falls es im passenden Bereich noch keine gibt. */
  newListName: string;
}

/**
 * Frist in die Planung: Aufgabe oder Termin über die gewöhnlichen Tabellen
 * (eigene Akte privat, Kinder-Akte geteilt im Haushalt), der Verweis am
 * Eintrag — alles in einem Schritt. Ohne Frist geschieht nichts.
 */
export async function planRecordDeadline(
  entry: LocalRecordEntry,
  draft: PlanDraft,
  database: KraftwerkDatabase = db,
): Promise<RecordPlan | null> {
  const due = recordDue(entry.kind, entry.fields);
  const title = draft.title.trim();
  if (!due || !title) return null;
  const now = stamp();
  const householdId = entry.householdId;
  const changes: RowChange[] = [];
  let plan: RecordPlan;

  if (draft.as === 'event') {
    const id = newId();
    const fields = {
      id,
      updatedAt: now,
      deletedAt: null,
      title,
      date: draft.date,
      time: null,
      durationMin: null,
      endDate: null,
      location: null,
      note: null,
      activity: null,
      activityMin: null,
      cancelled: false,
    };
    if (householdId) {
      changes.push({
        table: 'household_events',
        row: { ...fields, householdId, forUserIds: [entry.subjectId] } as LocalHouseholdEvent,
      });
      plan = { kind: 'household_event', id, date: due };
    } else {
      changes.push({ table: 'events', row: fields as LocalEvent });
      plan = { kind: 'event', id, date: due };
    }
  } else {
    const lists = (await database.task_lists.toArray()).filter(
      (list) =>
        !list.deletedAt && list.kind === 'tasks' && (list.householdId ?? null) === householdId,
    );
    let list = lists.sort((a, b) => a.position - b.position)[0];
    if (!list) {
      list = {
        id: newId(),
        householdId,
        name: draft.newListName.trim(),
        kind: 'tasks',
        position: (await database.task_lists.count()) || 0,
        updatedAt: now,
        deletedAt: null,
      } satisfies LocalTaskList;
      changes.push({ table: 'task_lists', row: list });
    }
    const position = (await database.tasks.where('listId').equals(list.id).count()) || 0;
    const id = newId();
    const task: LocalTask = {
      id,
      householdId,
      listId: list.id,
      parentId: null,
      title,
      note: null,
      dueDate: draft.date,
      dueTime: draft.time,
      repeat: null,
      assigneeId: null,
      doneAt: null,
      doneBy: null,
      position,
      updatedAt: now,
      deletedAt: null,
    } as LocalTask;
    changes.push({ table: 'tasks', row: task });
    plan = { kind: 'task', id, date: due };
  }

  changes.push({ table: 'record_entries', row: { ...entry, plan, updatedAt: now } });
  await writeRows(changes, database);
  return plan;
}

/** Steht der verknüpfte Eintrag in der Planung noch (nicht gelöscht)? */
export async function planStillThere(
  plan: RecordPlan | null,
  database: KraftwerkDatabase = db,
): Promise<boolean> {
  if (!plan) return false;
  const table =
    plan.kind === 'task'
      ? database.tasks
      : plan.kind === 'event'
        ? database.events
        : database.household_events;
  const row = await table.get(plan.id);
  return Boolean(row && !row.deletedAt);
}

/**
 * Wie viele Änderungen dieser Akte auf ihren Schlüssel warten — die eigene
 * auf den Kontoschlüssel, die eines Kindes auf den Haushaltsschlüssel. Sie
 * gehen nie als Klartext hinaus. Flach gelesen (Live-Abfrage).
 */
export async function heldRecordCount(
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<number> {
  const keyMeta = householdId ? `hk:${householdId}` : KEY_META;
  if (await database.meta.get(keyMeta)) return 0;
  return database.outbox
    .where('table')
    .equals('record_entries')
    .filter(
      (entry) =>
        ((entry.row as { householdId?: string | null }).householdId ?? null) === householdId,
    )
    .count();
}
